import { ModuleLayout } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router'

import { useSegment } from '../../../../hooks'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { VatDeclarationContextProvider } from '../../context/vatDeclarationContext'
import { useVatDeclarationsRoutes, VatDeclarationsRouteProvider } from '../../context/vatDeclarationsRouteContext'
import { VatDeclarationsParams } from '../../types/vatDeclarationsParams'
import { PromoBanner } from './elements/PromoBanner'
import { SuggestionsTable } from './elements/SuggestionsTable'
import { VatSuggestionsFormContextProvider } from './elements/SuggestionsTable/contexts/vatSuggestionsFormContext'
import { VatSuggestionsActions } from './elements/VatSuggestionsActions'
import { VatSuggestionsTitle } from './elements/VatSuggestionsTitle'

export const VatSuggestions = (): ReactElement | null => (
  <VatDeclarationsRouteProvider>
    <VatSuggestionsContent />
  </VatDeclarationsRouteProvider>
)

const VatSuggestionsContent = (): ReactElement | null => {
  const navigate = useNavigate()
  const { track } = useSegment()
  const { vatDeclarationId } = useParams<VatDeclarationsParams>()
  const vatDeclarationsRoutes = useVatDeclarationsRoutes()
  const { url: vatDeclarationUrl } = useOrganizationViewUrl(
    vatDeclarationsRoutes.getViewVatDeclarationRoute(vatDeclarationId),
  )

  const returnToDeclaration = useCallback(() => {
    if (!vatDeclarationUrl) {
      return
    }
    navigate(vatDeclarationUrl)
  }, [vatDeclarationUrl, navigate])

  const handleSkipClick = useCallback(() => {
    track('VAT Suggestions Skipped (Client)')
    returnToDeclaration()
  }, [returnToDeclaration, track])

  if (!vatDeclarationId) {
    return null
  }

  return (
    <VatDeclarationContextProvider vatDeclarationId={vatDeclarationId}>
      <VatSuggestionsFormContextProvider returnToDeclaration={returnToDeclaration}>
        <ModuleLayout
          headerContent={<VatSuggestionsTitle />}
          sideActions={<VatSuggestionsActions onSkipClick={handleSkipClick} />}
          withBackButton
          onBackButtonClick={returnToDeclaration}
        >
          <PromoBanner />
          <SuggestionsTable onBackButtonClick={returnToDeclaration} />
        </ModuleLayout>
      </VatSuggestionsFormContextProvider>
    </VatDeclarationContextProvider>
  )
}
